import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  SummarySection,
  PaperSection,
  CoreCompetenciesSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 16 - 2열 사이드바 레이아웃 (8.html 기반)
const Template16 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  const SectionTitle = ({ children }) => (
    <h2 className="text-lg font-bold tracking-wider mb-4 text-[#2d4a61] border-b-2 border-[#2d4a61] pb-2">
      {children}
    </h2>
  );

  return (
    <div className={`template-16 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <div className="flex">
        {/* Left Column - Main Content (65%) */}
        <div className="w-[65%]">
          {/* Header Section */}
          <div className="bg-[#2d4a61] px-10 py-8">
            <h1 className={getPlaceholderClass(hasRealData.name, "text-[38px] font-bold mb-2.5 text-white tracking-wider")}>
              {currentData.name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, "text-base text-white")}>
              {currentData.title}
            </p>
          </div>

          {/* Summary Section */}
          {currentData.summary && (
            <div className="px-10 py-8">
              <SummarySection
                summary={currentData.summary}
                hasRealData={hasRealData}
                textClassName="text-sm leading-[1.7]"
              />
            </div>
          )}

          {/* Experience Section */}
          {currentData.experiences.length > 0 && (
            <div className="px-10 py-5">
              <SectionTitle>{titles.experience.toUpperCase()}</SectionTitle>
              {currentData.experiences.map((exp, index) => (
                <div key={index} className="mb-5">
                  <p className={getPlaceholderClass(hasRealData.experiences, "text-[15px] font-bold mb-2")}>
                    {exp.position}, {exp.company} ({exp.startDate} - {exp.endDate})
                  </p>
                  
                  {exp.overallDescription && (
                    <p className={`text-sm leading-relaxed my-2.5 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                      {exp.overallDescription}
                    </p>
                  )}

                  {exp.projects && exp.projects.length > 0 && (
                    <div className="space-y-4">
                      {exp.projects.map((project, projIndex) => (
                        <div key={projIndex}>
                          <p className={getPlaceholderClass(hasRealData.experiences, "text-sm font-bold mb-2")}>
                            {project.projectName}
                          </p>
                          {project.projectDescription && (
                            <ul className="ml-5 list-disc list-outside">
                              {project.projectDescription.split('\n').map((line, lineIndex) => (
                                line.trim() && (
                                  <li key={lineIndex} className={`mb-1 text-sm leading-relaxed ${getPlaceholderClass(hasRealData.experiences)}`}>
                                    {line.trim()}
                                  </li>
                                )
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Education Section */}
          {currentData.education.length > 0 && (
            <div className="px-10 py-5">
              <SectionTitle>{titles.education.toUpperCase()}</SectionTitle>
              {currentData.education.map((edu, index) => (
                <div key={index}>
                  <p className={getPlaceholderClass(hasRealData.education, "text-[15px] font-bold mb-1")}>
                    {edu.degree}, {edu.school} ({edu.startDate} - {edu.endDate})
                  </p>
                  {edu.major && (
                    <p className={getPlaceholderClass(hasRealData.education, "text-sm")}>
                      {edu.major}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Papers Section */}
          {hasRealData.papers && currentData.papers.length > 0 && (
            <div className="px-10 py-5">
              <SectionTitle>{titles.papers.toUpperCase()}</SectionTitle>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                itemClassName="mb-4"
                titleClassName="text-[15px] font-bold"
                authorsClassName="text-sm text-gray-600"
                journalClassName="text-sm"
              />
            </div>
          )}
        </div>

        {/* Right Column - Sidebar (35%) */}
        <div className="w-[35%] bg-gray-100">
          {/* Contact Section */}
          <div className="px-8 py-8">
            <SectionTitle>{titles.contact.toUpperCase()}</SectionTitle>
            {currentData.phone && (
              <p className={getPlaceholderClass(hasRealData.phone, "text-sm leading-[1.8]")}>
                {currentData.phone}
              </p>
            )}
            {currentData.email && (
              <p className={getPlaceholderClass(hasRealData.email, "text-sm leading-[1.8]")}>
                {currentData.email}
              </p>
            )}
            {currentData.address && (
              <p className={getPlaceholderClass(hasRealData.address, "text-sm leading-[1.8]")}>
                {currentData.address}
              </p>
            )}
            {currentData.socialLinks && currentData.socialLinks.length > 0 && currentData.socialLinks.map((social, index) => (
              <p key={index} className={getPlaceholderClass(hasRealData.socialLinks, "text-sm leading-[1.8]")}>
                <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              </p>
            ))}
          </div>

          {/* Key Strengths Section */}
          {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
            <div className="px-8 pb-8">
              <SectionTitle>{titles.keyStrengths.toUpperCase()}</SectionTitle>
              <CoreCompetenciesSection
                keyStrengths={currentData.keyStrengths}
                hasRealData={hasRealData}
                bulletStyle="•"
                className="space-y-2.5"
                textClassName="text-sm leading-relaxed"
              />
            </div>
          )}

          {/* Certificates Section */}
          {currentData.certificates.length > 0 && (
            <div className="px-8 pb-8">
              <SectionTitle>{titles.certificates.toUpperCase()}</SectionTitle>
              {currentData.certificates.map((cert, index) => (
                <p key={index} className={getPlaceholderClass(hasRealData.certificates, "text-sm leading-[1.8] mb-2")}>
                  {cert.name} - {cert.issuer} ({cert.date})
                </p>
              ))}
            </div>
          )}

          {/* Skills Section */}
          {currentData.skills.length > 0 && (
            <div className="px-8 pb-8">
              <SectionTitle>{titles.skills.toUpperCase()}</SectionTitle>
              <div className={getPlaceholderClass(hasRealData.skills, "text-sm leading-[1.8]")}>
                {currentData.skills.map((skill, index) => (
                  <p key={index} className="mb-1">- {skill}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Template16;